import { useEffect, useState } from "react";
import { SectionCard } from "../../../../Component/AgentComponents/SectionCard";
import { FileText, Link2 } from "lucide-react";
import BlueButton from "../../../../Component/Common/BlueButton";
import toast from "react-hot-toast";
import { BASE_URL } from "../../../../config/api";

export default function ClaimConsole({
  selectedClaim,
  setSelectedClaim,
}) {
  

  /* ---------------- STATE ---------------- */
  const [estimateAmount, setEstimateAmount] = useState("");
  const [payableAmount, setPayableAmount] = useState(0);
  const [paymentLink, setPaymentLink] = useState("");
  const [inspectionRemark, setInspectionRemark] = useState("");

  const [reportItems, setReportItems] = useState([
    { label: "", amount: "" },
  ]);

  /* ---------------- HELPERS ---------------- */
  const addItem = () => {
    setReportItems([...reportItems, { label: "", amount: "" }]);
  };

  const updateItem = (index, key, value) => {
    const updated = [...reportItems];
    updated[index][key] = value;
    setReportItems(updated);
  };

  const removeItem = (index) => {
    setReportItems(reportItems.filter((_, i) => i !== index));
  };

  /* ---------------- AUTO TOTAL ---------------- */
  useEffect(() => {
    const total = reportItems.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );
    setPayableAmount(total);
  }, [reportItems]);

  /* ---------------- RESET ON CLAIM CHANGE ---------------- */
  useEffect(() => {
    setEstimateAmount("");
    setPayableAmount(0);
    setPaymentLink("");
    setInspectionRemark("");
    setReportItems([{ label: "", amount: "" }]);
  }, [selectedClaim]);

  /* ---------------- SUBMIT ---------------- */
  const handleSubmitInspection = async () => {
    if (!selectedClaim?.RawID) {
      toast.error("Invalid claim ID");
      return;
    }

    const finalPaymentLink =
      paymentLink || `https://pay.example.com/claim/${selectedClaim.RawID}`;

    const cleanReportItems = reportItems
      .filter((i) => i.label && i.amount)
      .map((i) => ({
        label: i.label,
        amount: Math.max(0, Number(i.amount)), // ✅ safety
      }));


    const payload = {
      claim_id: selectedClaim.RawID,
      report: cleanReportItems,
      estimate_amount: Number(estimateAmount),
      payable_amount: Number(payableAmount),
      inspection_remark: inspectionRemark || null, // ✅ NEW
      payment_link: finalPaymentLink,
      payment_status: "pending",
    };

    const toastId = toast.loading("Submitting inspection report...");

    try {
      const res = await fetch(`${BASE_URL}warranty/claim/inspection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (!json?.status) {
        throw new Error(json?.message || "Inspection failed");
      }

      toast.success(json.message || "Inspection submitted", { id: toastId });
      setSelectedClaim(null);
    } catch (err) {
      toast.error(err.message || "Failed to submit inspection", {
        id: toastId,
      });
    }
  };
  if (!selectedClaim) return null;

  return (
    <SectionCard
      title="Claim Inspection Console"
      right={
        <span className="text-sm text-slate-400 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Inspection
        </span>
      }
    >
      <div className="text-sm space-y-4">

        {/* ================= SUMMARY ================= */}
        <div className="flex justify-between">
          <div>
            <p className="font-semibold">
              {selectedClaim.id} · {selectedClaim.device}
            </p>
            <p className="text-slate-500">
              {selectedClaim.retailer} · {selectedClaim.customer}
            </p>
          </div>
          <div className="text-slate-500">
            Updated: {selectedClaim.lastUpdated}
          </div>
        </div>

        {/* ================= ESTIMATE ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ================= ESTIMATE ================= */}
          <div className="space-y-2">
            <label className="text-sm font-semibold">
              Estimate Amount (₹)
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={estimateAmount}
              onChange={(e) => {
                const value = e.target.value;

                // allow empty input
                if (value === "") {
                  setEstimateAmount("");
                  return;
                }

                // block negative numbers
                if (Number(value) < 0) return;

                setEstimateAmount(value);
              }}
              className="w-full border rounded-xl px-2 py-1 text-sm"
              placeholder="Enter estimate (optional)"
            />

          </div>

          {/* ================= DAMAGE REPORT ================= */}
          <div className="space-y-2">
            <p className="font-semibold">Damage Report</p>

            {reportItems.map((item, index) => (
              <div key={index} className="grid grid-cols-5 gap-2 items-center">
                <input
                  type="text"
                  placeholder="Damage / Part name"
                  value={item.label}
                  onChange={(e) =>
                    updateItem(index, "label", e.target.value)
                  }
                  className="col-span-3 border rounded-xl px-2 py-1 text-sm"
                />

                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="₹ Amount"
                  value={item.amount}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "") {
                      updateItem(index, "amount", "");
                      return;
                    }

                    if (Number(value) < 0) return;

                    updateItem(index, "amount", value);
                  }}
                  className="col-span-1 border rounded-xl px-2 py-1 text-sm"
                />
                
                {reportItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-red-500 text-xs"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addItem}
              className="text-blue-600 text-sm"
            >
              + Add more damage
            </button>
          </div>
        </div>

        {/* ================= INSPECTION REMARK ================= */}
        <div>
          <label className="text-sm font-semibold">
            Inspection Remark
          </label>
          <textarea
            rows={3}
            value={inspectionRemark}
            onChange={(e) => setInspectionRemark(e.target.value)}
            placeholder="Add technician / inspection remarks..."
            className="w-full border rounded-xl px-2 py-1 text-sm resize-none"
          />
        </div>
        
        {/* ================= PAYMENT SUMMARY ================= */}
        <div className="border rounded-xl p-3 bg-slate-50 space-y-2">
          <p className="font-semibold">Payment Summary</p>

          {reportItems
            .filter((i) => i.label && i.amount)
            .map((i, idx) => (
              <div key={idx} className="flex justify-between">
                <span>{i.label}</span>
                <span>₹{Number(i.amount).toLocaleString()}</span>
              </div>
            ))}

          <div className="border-t pt-2 flex justify-between font-semibold">
            <span>Total Payable</span>
            <span>₹{Number(payableAmount).toLocaleString()}</span>
          </div>
        </div>

        {/* ================= PAYMENT LINK ================= */}
        <div>
          <label className="text-sm font-semibold">Payment Link</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={paymentLink || "No link generated yet"}
              className="flex-1 border rounded-xl px-2 py-1 text-sm bg-slate-50"
            />
            <button
              type="button"
              onClick={() =>
                setPaymentLink(
                  `https://pay.example.com/claim/${selectedClaim.RawID}`
                )
              }
              className="flex items-center gap-1 text-sm px-2.5 py-1 rounded-xl border border-blue-200 bg-blue-50 hover:bg-blue-100"
            >
              <Link2 className="w-3 h-3" />
              Create link
            </button>
          </div>
        </div>

        {/* ================= ACTIONS ================= */}
        <div className="flex justify-end gap-2 pt-3">
          <BlueButton
            onClick={() => setSelectedClaim(null)}
            className="text-xs"
          >
            Cancel
          </BlueButton>
          <BlueButton onClick={handleSubmitInspection}>
            Submit Inspection
          </BlueButton>
        </div>
      </div>
    </SectionCard>
  );
}



// import { useEffect, useState } from "react";
// import { SectionCard } from "../../../../Component/AgentComponents/SectionCard";
// import { FileText, Link2 } from "lucide-react";
// import BlueButton from "../../../../Component/Common/BlueButton";
// import toast from "react-hot-toast";
// import { BASE_URL } from "../../../../config/api";

// export default function ClaimConsole({
//   selectedClaim,
//   setSelectedClaim,
//   handleUpdateClaim,
// }) {

//   console.log("selected claim in claim: ",selectedClaim)
//   // ✅ Local editable state
//   const [estimateAmount, setEstimateAmount] = useState("");
//   const [payableAmount, setPayableAmount] = useState("");
//   const [paymentLink, setPaymentLink] = useState("");
//   const [reportItems, setReportItems] = useState([
//   { label: "", amount: "" },
// ]);

//   const addItem = () => {
//   setReportItems([...reportItems, { label: "", amount: "" }]);
// };

// const updateItem = (index, key, value) => {
//   const updated = [...reportItems];
//   updated[index][key] = value;
//   setReportItems(updated);
// };

// const removeItem = (index) => {
//   setReportItems(reportItems.filter((_, i) => i !== index));
// };


//   // 🔁 Reset inputs when claim changes
//   useEffect(() => {
//     setEstimateAmount("");
//     setPayableAmount("");
//     setPaymentLink("");
//   }, [selectedClaim]);

//   const handleSubmitInspection = async () => {
//   if (!selectedClaim?.RawID) {
//     toast.error("Invalid claim ID");
//     return;
//   }

//   const finalPaymentLink =
//     paymentLink || `https://pay.example.com/claim/${selectedClaim.RawID}`;

//   const cleanReportItems = reportItems
//   .filter((i) => i.label && i.amount)
//   .map((i) => ({
//     label: i.label,
//     amount: Number(i.amount),
//   }));

//   const payload = {
//     claim_id: selectedClaim.RawID,
//     report: cleanReportItems,
//     estimate_amount: Number(estimateAmount),
//     payable_amount: Number(payableAmount),
//     payment_link: finalPaymentLink,
//     payment_status: "pending",
//   };

//   const toastId = toast.loading("Submitting inspection report...");

//   try {
//     const res = await fetch(`${BASE_URL}warranty/claim/inspection`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     });

//     const json = await res.json();

//     if (!json?.status) {
//       throw new Error(json?.message || "Inspection failed");
//     }

//     toast.success(json.message || "Inspection submitted", { id: toastId });

//     // ✅ CLOSE CONSOLE ONLY
//     setSelectedClaim(null);

//     // ❗ DO NOT update UI locally
//     // ❗ DO NOT read json.data
//     // ❗ Backend is source of truth

//   } catch (err) {
//     toast.error(err.message || "Failed to submit inspection", {
//       id: toastId,
//     });
//   }
// };


//   if (!selectedClaim) return null;
//   return (
//     <SectionCard
//       title="Claim Inspection Console"
//       right={
//         <span className="text-sm text-slate-400 flex items-center gap-1">
//           <FileText className="w-3 h-3" />
//           Inspection
//         </span>
//       }
//     >
//       <div className="text-sm space-y-4">
//         {/* Summary */}
//         <div className="flex justify-between">
//           <div>
//             <p className="font-semibold">
//               {selectedClaim.id} · {selectedClaim.device}
//             </p>
//             <p className="text-slate-500">
//               {selectedClaim.retailer} · {selectedClaim.customer}
//             </p>
//           </div>
//           <div className="text-slate-500">
//             Updated: {selectedClaim.lastUpdated}
//           </div>
//         </div>

//         {/* Amounts */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
//           <div>
//             <label className="text-sm">Estimate amount (₹)</label>
//             <input
//               type="number"
//               value={estimateAmount}
//               onChange={(e) => setEstimateAmount(e.target.value)}
//               className="w-full border rounded-xl px-2 py-1 text-sm"
//               placeholder="Enter estimate"
//             />
//           </div>

//           <div>
//             <label className="text-sm">Payable amount (₹)</label>
//             <input
//               type="number"
//               value={payableAmount}
//               onChange={(e) => setPayableAmount(e.target.value)}
//               className="w-full border rounded-xl px-2 py-1 text-sm"
//               placeholder="Enter payable"
//             />
//           </div>
//         </div>

//         {/* Damage Report */}
//         <div className="space-y-2">
//           <p className="font-semibold">Damage Report</p>

//           {reportItems.map((item, index) => (
//             <div key={index} className="grid grid-cols-5 gap-2 items-center">
//               <input
//                 type="text"
//                 placeholder="Damage / Part name"
//                 value={item.label}
//                 onChange={(e) =>
//                   updateItem(index, "label", e.target.value)
//                 }
//                 className="col-span-3 border rounded-xl px-2 py-1 text-sm"
//               />

//               <input
//                 type="number"
//                 placeholder="₹ Amount"
//                 value={item.amount}
//                 onChange={(e) =>
//                   updateItem(index, "amount", e.target.value)
//                 }
//                 className="col-span-1 border rounded-xl px-2 py-1 text-sm"
//               />

//               {reportItems.length > 1 && (
//                 <button
//                   onClick={() => removeItem(index)}
//                   className="text-red-500 text-xs"
//                 >
//                   ✕
//                 </button>
//               )}
//             </div>
//           ))}

//           <button
//             onClick={addItem}
//             className="text-blue-600 text-sm mt-1"
//           >
//             + Add more damage
//           </button>
//         </div>


//         {/* Payment Link */}
//         <div>
//           <label className="text-sm">Payment link</label>
//           <div className="flex items-center gap-2">
//             <input
//               type="text"
//               readOnly
//               value={paymentLink || "No link generated yet"}
//               className="flex-1 border rounded-xl px-2 py-1 text-sm bg-slate-50"
//             />
//             <button
//               type="button"
//               onClick={() =>
//                 setPaymentLink(
//                   `https://pay.example.com/claim/${selectedClaim.RawID}`
//                 )
//               }
//               className="flex items-center gap-1 text-sm px-2.5 py-1 rounded-xl border border-blue-200 bg-blue-50 hover:bg-blue-100"
//             >
//               <Link2 className="w-3 h-3" />
//               Create link
//             </button>
//           </div>
//         </div>

//         {/* Actions */}
//         <div className="flex justify-end gap-2 pt-3">
//           <BlueButton onClick={() => setSelectedClaim(null)} className="text-xs">
//             Cancel
//           </BlueButton>
//           <BlueButton onClick={handleSubmitInspection}>
//             Submit Inspection
//           </BlueButton>
//         </div>
//       </div>
//     </SectionCard>
//   );
// }




// import { SectionCard } from "../../../../Component/AgentComponents/SectionCard";
// import { FileText, Link2 } from "lucide-react";
// import BlueButton from "../../../../Component/Common/BlueButton";

// export default function ClaimConsole({
//   selectedClaim,
//   setSelectedClaim,
//   handleUpdateClaim,
// }) {
//   // 🔒 Guard: render nothing if no claim selected
//   if (!selectedClaim) return null;

//   return (
//     <SectionCard
//       title="Claim Handling Console"
//       right={
//         <span className="text-sm text-slate-400 flex items-center gap-1">
//           <FileText className="w-3 h-3" />
//           Admin-only
//         </span>
//       }
//     >
//       <div className="text-sm space-y-3">

//         {/* Summary */}
//         <div className="flex flex-wrap justify-between gap-2">
//           <div>
//             <p className="text-sm text-slate-500">Claim</p>
//             <p className="text-sm font-semibold">
//               {selectedClaim.id} · {selectedClaim.device}
//             </p>
//             <p className="text-sm text-slate-500">
//               {selectedClaim.retailer} · {selectedClaim.customer}
//             </p>
//           </div>

//           <div className="flex flex-col items-end text-sm text-slate-500">
//             <span>Last updated: {selectedClaim.lastUpdated}</span>
//             <span>Raised by: {selectedClaim.raisedBy}</span>
//           </div>
//         </div>

//         {/* Editable Fields */}
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
//           <div>
//             <label className="text-sm">Approved amount (₹)</label>
//             <input
//               type="number"
//               value={selectedClaim.approvedAmount ?? ""}
//               onChange={(e) =>
//                 handleUpdateClaim?.(selectedClaim.id, {
//                   approvedAmount: Number(e.target.value) || 0,
//                 })
//               }
//               className="w-full border rounded-xl px-2 py-1 text-xs"
//             />
//           </div>

//           <div>
//             <label className="text-sm">Payable amount (₹)</label>
//             <input
//               type="number"
//               value={selectedClaim.payableAmount ?? ""}
//               onChange={(e) =>
//                 handleUpdateClaim?.(selectedClaim.id, {
//                   payableAmount: Number(e.target.value) || 0,
//                 })
//               }
//               className="w-full border rounded-xl px-2 py-1 text-xs"
//             />
//           </div>

//           <div>
//             <label className="text-sm">Claim status</label>
//             <select
//               value={selectedClaim.status}
//               onChange={(e) =>
//                 handleUpdateClaim?.(selectedClaim.id, {
//                   status: e.target.value,
//                 })
//               }
//               className="w-full border rounded-xl px-2 py-1 text-sm bg-white"
//             >
//               <option value="Pending">Pending</option>
//               <option value="In Progress">In Progress</option>
//               <option value="Approved">Approved</option>
//               <option value="Rejected">Rejected</option>
//               <option value="Settled">Settled</option>
//             </select>
//           </div>
//         </div>

//         {/* Reason */}
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
//           <div className="md:col-span-2">
//             <label className="text-sm">Rejection / internal reason</label>
//             <input
//               type="text"
//               value={selectedClaim.reason || ""}
//               onChange={(e) =>
//                 handleUpdateClaim?.(selectedClaim.id, {
//                   reason: e.target.value,
//                 })
//               }
//               className="w-full border rounded-xl px-2 py-1 text-xs"
//             />
//           </div>
//         </div>

//         {/* Payment Link */}
//         <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr] gap-3 items-end">
//           <div>
//             <label className="text-sm">Payment link</label>
//             <div className="flex items-center gap-2">
//               <input
//                 type="text"
//                 readOnly
//                 value={selectedClaim.paymentLink || "No link generated yet"}
//                 className="flex-1 border rounded-xl px-2 py-1 text-sm bg-slate-50"
//               />

//               <button
//                 type="button"
//                 onClick={() =>
//                   handleUpdateClaim?.(selectedClaim.id, {
//                     paymentLink: `https://pay.goex.example/${selectedClaim.id}`,
//                     payableStatus: "Payment Link Created",
//                   })
//                 }
//                 className="flex items-center gap-1 text-sm px-2.5 py-1 rounded-xl border border-blue-200 bg-blue-50 hover:bg-blue-100"
//               >
//                 <Link2 className="w-3 h-3" />
//                 Create link
//               </button>
//             </div>
//           </div>

//           <div className="text-sm text-slate-500">
//             <p className="mb-1 font-semibold">Flow notes</p>
//             <p>
//               Remaining payout collection and follow-ups happen on retailer /
//               agent dashboards.
//             </p>
//           </div>
//         </div>

//         <BlueButton
//           onClick={() => setSelectedClaim(null)}
//           className="flex justify-end text-xs text-slate-500 hover:text-slate-700"
//         >
//           Clear Selection
//         </BlueButton>
//       </div>
//     </SectionCard>
//   );
// }





// import { SectionCard } from "../../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../../Component/Common/BlueButton";


// export default function ClaimAdminConsole({ claim, onClear, onUpdate }) {
//   return (
//     <SectionCard title="Claim Handling Console">
//       <div className="space-y-4 text-sm">

//         <div>
//           <p className="font-semibold">{claim.id}</p>
//           <p className="text-slate-500">{claim.device}</p>
//         </div>

//         <input
//           type="number"
//           value={claim.approvedAmount}
//           onChange={(e) =>
//             onUpdate({ ...claim, approvedAmount: Number(e.target.value) })
//           }
//           className="border rounded px-2 py-1"
//         />

//         <select
//           value={claim.status}
//           onChange={(e) => onUpdate({ ...claim, status: e.target.value })}
//           className="border rounded px-2 py-1"
//         >
//           <option>Pending</option>
//           <option>Approved</option>
//           <option>Rejected</option>
//         </select>

//         <BlueButton onClick={onClear}>Clear Selection</BlueButton>
//       </div>
//     </SectionCard>
//   );
// }
